import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { LogoMark } from '../../logo/logo-mark';
import { RightArrow } from '../../icons/right-arrow';
import { useAuth } from '../../../context/auth-provider';
import { publicApi } from '../../../api/public-client';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { roleLabel } from '../../../auth/roles';
import {
  AutoHeight,
  Collapse,
  Shake,
  StateSwitch,
  StatusLabel,
} from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  Field,
  Label,
  PasswordWrap,
  PasswordInput,
  PasswordToggle,
  FullWidthButton,
  FieldError,
  HelperText,
  InfoBanner,
  TopLink,
  HeadingSkeleton,
  FieldSkeleton,
  SkeletonButton,
  SkeletonGroup,
} from '../onboarding.styled';

type LoadState = 'loading' | 'ready' | 'expired' | 'invalid';

export const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { signIn, endSession, user, authStatus } = useAuth();

  const [state, setState] = useState<LoadState>('loading');
  const [invite, setInvite] = useState<{
    role: string;
    first_name: string;
    email_masked: string;
  } | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Set when this page signed somebody else out to make room for the invitee, so
  // the admin who clicked their own invite link is TOLD their session ended
  // rather than discovering it later on a 401.
  const [displaced, setDisplaced] = useState('');
  const ran = useRef(false);
  const handedOff = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    publicApi
      .get(`/invitations/${token}`)
      .then(res => {
        setInvite(res.data);
        setState('ready');
      })
      .catch(err => {
        setState(err?.response?.status === 410 ? 'expired' : 'invalid');
      });
  }, [token]);

  // THE HANDOFF. An invite link is a credential for a DIFFERENT person than
  // whoever this browser is currently signed in as — most often the admin who
  // just sent it. Until this existed the router simply bounced them to their own
  // dashboard and the password form never rendered, so no invitation of any role
  // could be accepted from a browser that had ever logged in.
  //
  // Deliberately AFTER the preview resolves to 'ready': a dead or already-used
  // link must not log anybody out. Once the invitation is known good, the
  // incumbent session is revoked server-side (not merely forgotten client-side)
  // so the two identities can never overlap, and the accept response below is
  // what puts a session back.
  useEffect(() => {
    if (state !== 'ready' || authStatus !== 'authed' || !user) return;
    if (handedOff.current) return;
    handedOff.current = true;
    setDisplaced(user.full_name || user.email || 'the previous user');
    endSession();
  }, [state, authStatus, user, endSession]);

  const refuse = (message: string) => {
    setError(message);
    setAttempt(n => n + 1);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      refuse('Password must be at least 8 characters');

      return;
    }
    if (password !== confirm) {
      refuse('Passwords do not match');

      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await publicApi.post(`/invitations/${token}/accept`, {
        password,
      });
      // Session cookies (httpOnly) were set by the accept response itself.
      // signIn also moves authStatus to 'authed'; setting the pieces separately
      // leaves the router bouncing between '/' and the destination.
      //
      // The session arriving now is the INVITEE's own, so the handoff above
      // is done. Marked BEFORE signIn: signIn makes this still-mounted page
      // 'authed', and the handoff effect would otherwise take the new user
      // for an incumbent and end the session this accept just created
      // (production, 2026-09-24: every accept from a signed-out browser
      // landed back on the sign-in screen).
      handedOff.current = true;
      signIn(
        res.data.user,
        res.data.onboarding || null,
        res.data.permissions || []
      );
      navigate(
        resolvePostAuthRoute(
          res.data.user,
          res.data.onboarding,
          res.data.permissions || []
        )
      );
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      const status = ax.response?.status;
      if (status === 410) setState('expired');
      else if (status === 409)
        // The server says WHICH conflict it is — already used, an address that
        // already has an account (F23), a full unit pool, a resident record no
        // longer free. One fixed sentence for all of them was wrong for most.
        refuse(
          ax.response?.data?.message ||
            'This invitation was already used. Try signing in.'
        );
      else if (status === 404) setState('invalid');
      else refuse('Could not accept the invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        {/* The loading card used to be a single line of helper text that then
            jumped to a full form. The placeholder is now the form's own shape,
            and the four states crossfade inside one card that tweens its
            height. */}
        <AutoHeight>
          <StateSwitch state={state}>
            {state === 'loading' && (
              <SkeletonGroup label="Loading your invitation">
                <HeadingSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <SkeletonButton aria-hidden="true" />
              </SkeletonGroup>
            )}

            {(state === 'expired' || state === 'invalid') && (
              <>
                <Heading>
                  {state === 'expired'
                    ? 'This invitation has expired'
                    : 'This invitation isn’t valid'}
                </Heading>
                <Subheading>
                  {state === 'expired'
                    ? 'Ask whoever invited you to send a fresh invite.'
                    : 'It may have already been used. If you have an account, sign in.'}
                </Subheading>
                <TopLink>
                  <Link to="/">Go to sign in</Link>
                </TopLink>
              </>
            )}

            {state === 'ready' && (
              <>
                <Heading>
                  {invite?.first_name
                    ? `Welcome, ${invite.first_name}`
                    : 'Accept your invitation'}
                </Heading>
                <Subheading>
                  You’ve been invited to Lobbero as a{' '}
                  <strong>{roleLabel(invite?.role)}</strong>. Set a password to
                  activate your account.
                </Subheading>
                <InfoBanner>Invitation for {invite?.email_masked}</InfoBanner>
                {/* Arrives AFTER the form, when the handoff runs — it unfolds
                    rather than shoving the fields down. */}
                <Collapse open={!!displaced}>
                  <HelperText>
                    {displaced} was signed out on this device so this invitation
                    could be activated.
                  </HelperText>
                </Collapse>

                <form onSubmit={submit}>
                  <Field>
                    <Label htmlFor="inv-pass">Choose a password</Label>
                    <Shake active={!!error} attempt={attempt}>
                      <PasswordWrap>
                        <PasswordInput
                          id="inv-pass"
                          type={show ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          hasError={!!error}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          aria-describedby="password-rule"
                        />
                        <PasswordToggle
                          type="button"
                          onClick={() => setShow(s => !s)}
                        >
                          <StatusLabel
                            state={show ? 'hide' : 'show'}
                            labels={{ show: 'Show', hide: 'Hide' }}
                          />
                        </PasswordToggle>
                      </PasswordWrap>
                    </Shake>
                    {/* THE RULE, BEFORE THE ATTEMPT.
                        The only password requirement this product enforces is
                        length, and it was communicated ONLY as a placeholder —
                        which vanishes the moment the field is typed in, exactly
                        when it is needed, and which a screen reader announces
                        once and never again. A rule the user is told about
                        after being refused is a rule they meet by trial and
                        error. */}
                    <HelperText id="password-rule">
                      At least 8 characters. Spaces are allowed and count — a
                      short sentence makes a strong password.
                    </HelperText>
                  </Field>
                  <Field>
                    <Label htmlFor="inv-confirm">Confirm password</Label>
                    <Shake active={!!error} attempt={attempt}>
                      <PasswordInput
                        id="inv-confirm"
                        type={show ? 'text' : 'password'}
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        hasError={!!error}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                      />
                    </Shake>
                  </Field>
                  <FieldError message={error || undefined} />
                  <FullWidthButton type="submit" disabled={submitting}>
                    <StatusLabel
                      state={submitting ? 'busy' : 'idle'}
                      labels={{ idle: 'Activate account', busy: 'Activating…' }}
                    />{' '}
                    <RightArrow />
                  </FullWidthButton>
                </form>
              </>
            )}
          </StateSwitch>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
